import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import { parseSizeList, type SizeOption } from '@/features/customization/utils/parseSizeList';

export interface PdpEffectiveProductVm {
  activeCustomizationId: number;
  sizeList: SizeOption[];
  price: number | null;
  discountedPrice: number | null;
  discountValue: number | null;
  discountPercent: number | null;
  discountName: string | null;
  flatDiscountPercent: number | null;
  goldPrice: number | null;
  diamondPrice: number | null;
  diamondPriceAfterDiscount: number | null;
  discountOnDiamondPriceValue: number | null;
  gemstonePrice: number | null;
  presetSolitairePrice: number | null;
  makingChargePrice: number | null;
  makingChargePriceAfterDiscount: number | null;
  makingChargeDiscountValue: number | null;
  gst: number | null;
  goldPurity: string | null;
  metal: string;
  metalType: string;
  metalWeight: number;
  productWeight: number | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  gemstonesTypeAndCount: string | null;
}

/** Customization API may omit fields; mapper coerces missing strings to ''. */
function resolveCustomizationString(
  customized: string | null | undefined,
  fallback: string
): string {
  return customized?.trim() ? customized : fallback;
}

export function toPdpEffectiveProductVm(
  productDetail: ProductDetail,
  customization: ProductCustomization | null
): PdpEffectiveProductVm {
  return {
    activeCustomizationId: customization?.customizationId ?? productDetail.customizationId,
    sizeList: parseSizeList(productDetail.customizationOptions),
    price: customization?.price ?? productDetail.price,
    discountedPrice: customization?.discountedPrice ?? productDetail.discountedPrice,
    discountValue: customization?.discountValue ?? productDetail.discountValue,
    discountPercent: customization?.discountPercent ?? productDetail.discountPercent,
    discountName: customization?.discountName ?? productDetail.discountName,
    flatDiscountPercent: customization?.flatDiscountPercent ?? productDetail.flatDiscountPercent,
    goldPrice: customization?.goldPrice ?? productDetail.goldPrice,
    diamondPrice: customization?.diamondPrice ?? productDetail.diamondPrice,
    diamondPriceAfterDiscount:
      customization?.diamondPriceAfterDiscount ?? productDetail.diamondPriceAfterDiscount,
    discountOnDiamondPriceValue:
      customization?.discountOnDiamondPriceValue ?? productDetail.discountOnDiamondPriceValue,
    gemstonePrice: customization?.gemstonePrice ?? productDetail.gemstonePrice,
    presetSolitairePrice: customization?.presetSolitairePrice ?? productDetail.presetSolitairePrice,
    makingChargePrice: customization?.makingChargePrice ?? productDetail.makingChargePrice,
    makingChargePriceAfterDiscount:
      customization?.makingChargePriceAfterDiscount ?? productDetail.makingChargePriceAfterDiscount,
    makingChargeDiscountValue:
      customization?.makingChargeDiscountValue ?? productDetail.makingChargeDiscountValue,
    gst: customization?.gst ?? productDetail.gst,
    goldPurity:
      resolveCustomizationString(customization?.goldPurity, productDetail.goldPurity ?? '') ||
      null,
    metal: resolveCustomizationString(customization?.metal, productDetail.metal),
    metalType: resolveCustomizationString(customization?.metalType, productDetail.metalType),
    metalWeight: customization?.metalWeight ?? productDetail.metalWeight,
    productWeight: customization?.productWeight ?? productDetail.productWeight,
    diamondStoneType: customization?.diamondStoneType ?? productDetail.diamondStoneType,
    solitaireCarat: customization?.solitaireCarat ?? productDetail.solitaireCarat,
    gemstonesTypeAndCount:
      customization?.gemstonesTypeAndCount ?? productDetail.gemstonesTypeAndCount,
  };
}

export type { SizeOption as PdpSizeOption };
